// Mesajlar — sol bölme: arama + Tümü/Okunmamış çipleri + konuşma satırları.

import React from "react";
import { Avatar, Box, ButtonBase, Skeleton, Typography, alpha } from "@mui/material";
import { ForumOutlined as ForumIcon } from "@mui/icons-material";
import { ChoiceChipGroup, EmptyState, SearchField } from "../../components/atoms";
import { COLORS, TYPO } from "../../styles/uiConstants";
import { avatarTone, fullName, initialsOf } from "../Secretary/PatientsPage/patientListFormat";
import { listTimeLabel, previewLine } from "./messageFormat";
import useTerms from "../../hooks/useTerms";

function ConversationRow({ conversation, selected, onSelect, palette }) {
  const unread = conversation.unread || 0;
  const draft = Boolean(conversation.draft);
  const tone = avatarTone(conversation.patient);
  return (
    <ButtonBase
      onClick={() => onSelect(conversation)}
      aria-current={selected ? "true" : undefined}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: { xs: 2, md: 2.5 },
        py: 1.5,
        textAlign: "left",
        borderBottom: `1px solid ${COLORS.slate100}`,
        bgcolor: selected ? alpha(palette.primary, 0.06) : "transparent",
        boxShadow: selected ? `inset 3px 0 0 ${palette.primary}` : "none",
        "&:hover": { bgcolor: selected ? alpha(palette.primary, 0.08) : alpha(palette.primary, 0.03) },
      }}
    >
      <Avatar sx={{ width: 38, height: 38, fontSize: "0.8125rem", fontWeight: 600, bgcolor: alpha(tone, 0.14), color: tone }}>
        {initialsOf(conversation.patient)}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <Typography noWrap sx={{ flex: 1, fontSize: TYPO.body, fontWeight: unread ? 600 : 500, color: COLORS.slate800 }}>
            {fullName(conversation.patient)}
          </Typography>
          {draft ? (
            <Box
              component="span"
              sx={{
                flexShrink: 0,
                px: 0.75,
                py: 0.125,
                borderRadius: 1,
                fontSize: TYPO.caption,
                color: palette.primary,
                bgcolor: alpha(palette.primary, 0.1),
              }}
            >
              Yeni mesaj
            </Box>
          ) : (
            <Typography sx={{ flexShrink: 0, fontSize: TYPO.caption, color: unread ? palette.primary : COLORS.slate400 }}>
              {listTimeLabel(conversation.lastMessageAt)}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25 }}>
          <Typography
            noWrap
            sx={{
              flex: 1,
              fontSize: TYPO.label,
              color: unread ? COLORS.slate700 : COLORS.slate500,
              fontStyle: draft ? "italic" : "normal",
            }}
          >
            {draft ? "Yazılıyor… henüz gönderilmedi" : previewLine(conversation)}
          </Typography>
          {unread ? (
            <Box
              component="span"
              aria-label={`${unread} okunmamış mesaj`}
              sx={{
                flexShrink: 0,
                minWidth: 20,
                height: 20,
                px: 0.75,
                borderRadius: 10,
                bgcolor: palette.primary,
                color: COLORS.white,
                fontSize: TYPO.caption,
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {unread > 99 ? "99+" : unread}
            </Box>
          ) : null}
        </Box>
      </Box>
    </ButtonBase>
  );
}

function ListSkeleton() {
  return Array.from({ length: 6 }).map((_, i) => (
    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, px: { xs: 2, md: 2.5 }, py: 1.5, borderBottom: `1px solid ${COLORS.slate100}` }}>
      <Skeleton variant="circular" width={38} height={38} />
      <Box sx={{ flex: 1 }}>
        <Skeleton width="55%" height={18} />
        <Skeleton width="85%" height={16} />
      </Box>
    </Box>
  ));
}

/**
 * @param {object[]} conversations
 * @param {boolean} loading - ilk yükleme
 * @param {string|null} selectedId
 * @param {(c: object) => void} onSelect
 * @param {string} search
 * @param {(v: string) => void} onSearchChange
 * @param {"all"|"unread"} filter
 * @param {(v: string) => void} onFilterChange
 * @param {{all: number, unread: number}} counts
 * @param {number} total
 * @param {object} palette
 * @param {React.ReactNode} [emptyAction] - boş durum açıklamasındaki satır içi bağlantı
 */
export default function ConversationList({
  conversations,
  loading,
  selectedId,
  onSelect,
  search,
  onSearchChange,
  filter,
  onFilterChange,
  counts,
  total,
  palette,
  emptyAction,
}) {
  const terms = useTerms();
  const q = search.trim();
  const empty = !loading && conversations.length === 0;

  let emptyState = null;
  if (empty) {
    if (q) {
      emptyState = (
        <EmptyState
          variant="illustrated"
          icon={<ForumIcon />}
          title="Konuşma bulunamadı"
          description={`“${q}” aramanızla eşleşen konuşma yok. Aramayı değiştirin veya temizleyin.`}
        />
      );
    } else if (filter === "unread") {
      emptyState = (
        <EmptyState
          variant="illustrated"
          icon={<ForumIcon />}
          title="Okunmamış mesaj yok"
          description={`${terms.patients}ınızdan gelen tüm mesajları okudunuz.`}
        />
      );
    } else {
      emptyState = (
        <EmptyState
          variant="illustrated"
          icon={<ForumIcon />}
          title="Henüz mesaj yok"
          description={
            <>
              {terms.patients}ınızın uygulamadan yazdığı mesajlar burada görünür. Yazışma başlatmak için {terms.patientLower}
              sayfasındaki “İletişim › Mesajlar” bölümünü kullanın{emptyAction ? <> veya {emptyAction}</> : null}.
            </>
          }
        />
      );
    }
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
      <Box sx={{ px: { xs: 2, md: 2.5 }, pt: 2.5, flexShrink: 0 }}>
        <SearchField
          placeholder={`${terms.patient} adıyla ara`}
          value={search}
          onChange={onSearchChange}
          autoComplete="off"
          fullWidth
        />
      </Box>
      <Box sx={{ px: { xs: 2, md: 2.5 }, pt: 1.5, pb: 1.5, flexShrink: 0, borderBottom: `1px solid ${COLORS.slate100}` }}>
        <ChoiceChipGroup
          ariaLabel="Konuşma filtresi"
          options={[
            { value: "all", label: `Tümü · ${counts?.all ?? 0}` },
            { value: "unread", label: `Okunmamış · ${counts?.unread ?? 0}` },
          ]}
          value={filter}
          onChange={onFilterChange}
        />
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain" }}>
        {loading ? <ListSkeleton /> : null}
        {emptyState}
        {conversations.map((c) => (
          <ConversationRow
            key={c._id}
            conversation={c}
            selected={String(c._id) === String(selectedId)}
            onSelect={onSelect}
            palette={palette}
          />
        ))}
        {total > conversations.length ? (
          <Typography sx={{ px: 2.5, py: 1.5, fontSize: TYPO.caption, color: COLORS.slate400, textAlign: "center" }}>
            En son {conversations.length} konuşma gösteriliyor; diğerleri için aramayı kullanın.
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}
